import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import PaidPlanModal from "@/components/PaidPlanModal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Zap,
  Timer,
  FileText,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Send,
  SkipForward,
  BookOpen,
  Target,
  TrendingUp,
  Lightbulb,
  RefreshCw,
  Home,
  Flame,
  Star,
  BarChart3,
  BarChart2,
  AlertTriangle,
  ClipboardList,
  Brain,
  ChevronDown,
  ChevronUp,
  ArrowRight,
} from "lucide-react";
import falcoLogo from "@/assets/falco.jpeg";

// ── constants ────────────────────────────────────────────────

const READINESS_CONFIG = {
  exam_ready: { label: "Exam Ready",  color: "text-emerald-700 bg-emerald-100 border-emerald-200" },
  borderline:  { label: "Borderline", color: "text-amber-700 bg-amber-100 border-amber-200" },
  needs_work:  { label: "Needs Work", color: "text-orange-700 bg-orange-100 border-orange-200" },
  at_risk:     { label: "At Risk",    color: "text-red-700 bg-red-100 border-red-200" },
};

const REASON_CODE_LABELS = {
  MISS_DEF:              "Definition missing or incomplete",
  MISS_CLASS:            "Classification not provided",
  MISS_MECH:             "Mechanism of action not explained",
  MISS_CLIN:             "Clinical correlation absent",
  MISS_EXAMPLE:          "No example provided where one was expected",
  POOR_STRUCT:           "Answer lacks clear headings or logical sequence",
  WRONG_TERM:            "Incorrect or imprecise terminology used",
  WRONG_FACT:            "Factually incorrect statement present",
  MISS_DIAGRAM:          "Mandatory diagram not included",
  MISS_LABELS:           "Diagram present but key labels missing",
  IRRELEVANT:            "Answer contains significant irrelevant content",
  INCOMPLETE:            "Answer correct in direction but insufficiently detailed",
  MISS_KEYWORDS:         "Expected anatomical/medical keywords absent",
  NON_RESPONSIVE_ANSWER: "Answer does not address the question asked",
  INCOHERENT_ANSWER:     "Answer is incoherent or consists of repeated words",
  BLANK_ANSWER:          "Answer is too short or blank",
  QUESTION_REPEATED:     "Answer only repeats the question",
  WRONG_TOPIC:           "Answer is off-topic",
  SCORE_CAPPED:          "Score capped due to missing required component",
  STUDENT_DISTRESS:      "Student may need support",
  MISSING_MANDATORY_POINT: "Missing Required Point",
  INCOMPLETE_MECHANISM:    "Mechanism of action not explained",
  VAGUE_CONTENT:           "Answer correct in direction but insufficiently detailed",
  MISSING_CLINICAL:        "Clinical correlation absent",
  MISSING_DIAGRAM:         "Missing Diagram",
  POOR_STRUCTURE:          "Poor Structure",
  INCOMPLETE_COVERAGE:     "Incomplete Coverage",
  TERMINOLOGY_ERROR:       "Terminology Error",
};

// ── helpers ──────────────────────────────────────────────────

const getTimeForQuestion = (marks) => {
  const m = marks || 5;
  if (m <= 3)  return 5  * 60;
  if (m <= 5)  return 10 * 60;
  if (m <= 10) return 20 * 60;
  return 25 * 60;
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const pctColors = (pct) => {
  if (pct >= 70) return { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", badge: "bg-emerald-500", icon: <CheckCircle className="w-8 h-8 text-white" /> };
  if (pct >= 50) return { bg: "bg-amber-50",   border: "border-amber-300",   text: "text-amber-700",   badge: "bg-amber-500",   icon: <AlertCircle className="w-8 h-8 text-white" /> };
  return           { bg: "bg-red-50",     border: "border-red-300",     text: "text-red-700",     badge: "bg-red-500",     icon: <XCircle className="w-8 h-8 text-white" /> };
};

// ── helper: dynamic section heading for reason codes ─────────
const reasonSectionLabel = (scoreAwarded, totalMarks) => {
  if (scoreAwarded === 0)            return "Why No Marks Were Awarded";
  if (scoreAwarded < totalMarks)     return "Why Full Marks Were Not Awarded";
  return "Evaluation Notes";
};

// ── helper: filter breakdown entries ─────────────────────────
const filterBreakdown = (breakdown, question) => {
  const hasDiagram = !!(
    question?.diagram?.diagram_asset_url ||
    question?.diagram?.expected_labels?.length
  );
  return Object.entries(breakdown).filter(
    ([key, value]) => !(key === "clinical_diagram" && value === 0 && !hasDiagram)
  );
};

// ── Detailed Evaluation Modal ─────────────────────────────────

function DetailedEvalModal({ open, onClose, feedback, question }) {
  const [vivaOpen, setVivaOpen] = useState(false);
  if (!open || !feedback) return null;

  const pct = feedback.percentage || Math.round((feedback.score_awarded / feedback.total_marks) * 100) || 0;
  const colors = pctColors(pct);
  const readiness = READINESS_CONFIG[feedback.readiness_signal] || READINESS_CONFIG.needs_work;
  const pf = (feedback.feedback && typeof feedback.feedback === "object") ? feedback.feedback : null;
  const breakdown = feedback.mark_breakdown_awarded || {};
  const filteredBreakdown = filterBreakdown(breakdown, question);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        <DialogTitle className="sr-only">Detailed Evaluation</DialogTitle>
        <DialogDescription className="sr-only">Full breakdown of marks, points covered, and feedback for this attempt.</DialogDescription>

        <div className="bg-[#1A2236] px-6 py-5 rounded-t-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-[#C9A227] uppercase tracking-widest">Detailed Evaluation</span>
            {feedback.readiness_signal && (
              <span className={`text-xs font-bold px-3 py-1 rounded-full border ${readiness.color}`}>
                {readiness.label}
              </span>
            )}
          </div>
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-4xl font-mono font-bold text-white">
                {feedback.score_awarded}
                <span className="text-xl text-slate-400 font-normal"> / {feedback.total_marks}</span>
              </p>
              {question?.question_text && (
                <p className="text-slate-400 text-sm mt-1 leading-snug line-clamp-2 max-w-sm">
                  {question.question_text.substring(0, 90)}…
                </p>
              )}
            </div>
            <div className={`w-20 h-20 rounded-full ${colors.badge} flex flex-col items-center justify-center flex-shrink-0`}>
              <span className="text-white font-mono font-bold text-xl leading-none">{pct}%</span>
              <span className="text-white/70 text-xs mt-0.5">score</span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {filteredBreakdown.length > 0 && (
            <section>
              <h4 className="text-xs font-bold text-[#2E3A59] uppercase tracking-widest mb-3 flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-[#C9A227]" /> Marks Breakdown
              </h4>
              <div className={`grid gap-2 ${filteredBreakdown.length === 3 ? "grid-cols-3" : "grid-cols-4"}`}>
                {filteredBreakdown.map(([key, val]) => (
                  <div key={key} className={`text-center p-3 rounded-xl border-2 ${val > 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                    <div className={`font-mono text-lg font-bold ${val > 0 ? "text-emerald-700" : "text-red-500"}`}>{val}</div>
                    <div className="text-xs text-slate-500 mt-0.5 capitalize leading-tight">{key.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {(feedback.points_covered?.length > 0 || feedback.points_missed?.length > 0) && (
            <section>
              <h4 className="text-xs font-bold text-[#2E3A59] uppercase tracking-widest mb-3 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-[#C9A227]" /> Point-by-Point
              </h4>
              <div className="space-y-1.5">
                {feedback.points_covered?.map((pt, i) => (
                  <div key={`c${i}`} className="flex items-start gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-emerald-800 font-medium">{pt}</span>
                  </div>
                ))}
                {feedback.points_missed?.map((pt, i) => (
                  <div key={`m${i}`} className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-red-800 font-medium">{pt}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {feedback.reason_codes_triggered?.length > 0 && (
            <section>
              <h4 className="text-xs font-bold text-[#2E3A59] uppercase tracking-widest mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#C9A227]" />
                {reasonSectionLabel(feedback.score_awarded, feedback.total_marks)}
              </h4>
              <div className="flex flex-wrap gap-2">
                {[...new Set(feedback.reason_codes_triggered)].map((rc, i) => {
                  const code = typeof rc === "string" ? rc : rc.code;
                  const detail = typeof rc === "object" ? rc.detail : null;
                  return (
                    <div key={i} className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        {REASON_CODE_LABELS[code] || code}
                      </span>
                      {detail && <p className="text-xs text-red-600 mt-1">{detail}</p>}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {pf && (
            <section className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 bg-[#2E3A59] flex items-center gap-2">
                <Brain className="w-4 h-4 text-[#C9A227]" />
                <span className="text-sm font-bold text-white">Personalised Feedback</span>
              </div>
              <div className="p-4 space-y-4 bg-[#F8FAFC]">
                {pf.summary && (
                  <p className="text-[15px] text-slate-700 leading-relaxed border-l-4 border-[#C9A227] pl-4 italic">
                    "{pf.summary}"
                  </p>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  {pf.strengths?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-2">✓ What you got right</p>
                      <ul className="space-y-1.5">
                        {pf.strengths.map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                            <span className="text-emerald-500 flex-shrink-0 mt-0.5">•</span>{s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pf.gaps?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-red-600 uppercase tracking-wide mb-2">✗ What was missing</p>
                      <ul className="space-y-1.5">
                        {pf.gaps.map((g, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                            <span className="text-red-400 flex-shrink-0 mt-0.5">•</span>{g}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {pf.examiner_tip && (
                  <div className="flex items-start gap-3 p-3 bg-[#FDF8E6] border border-[#C9A227]/30 rounded-lg">
                    <Star className="w-4 h-4 text-[#C9A227] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-[#A6851D] uppercase tracking-wide mb-1">Examiner's Tip</p>
                      <p className="text-sm text-[#7A5E10]">{pf.examiner_tip}</p>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {feedback.viva_extras?.length > 0 && (
            <section>
              <button
                onClick={() => setVivaOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-[#FDF8E6] border-2 border-slate-200 hover:border-[#C9A227]/40 rounded-xl transition-all"
              >
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-[#C9A227]" />
                  <span className="text-sm font-bold text-[#2E3A59]">💡 Viva Extras & Deeper Points</span>
                </div>
                {vivaOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {vivaOpen && (
                <div className="mt-2 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl space-y-2 animate-in slide-in-from-top-2 duration-200">
                  {feedback.viva_extras.map((v, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-blue-800">
                      <span className="text-blue-400 flex-shrink-0 mt-0.5">•</span>{v}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <div className="pt-2 border-t border-slate-100">
            <Button onClick={onClose} className="w-full bg-[#2E3A59] hover:bg-[#1A2236] font-semibold h-11">
              <ArrowRight className="w-4 h-4 mr-2" /> Close & Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── DiagramPanel ──────────────────────────────────────────────────────────────

const DiagramPanel = ({ diagram, labels, onLabelChange, feedbackMode }) => {
  if (!diagram) return null;
  if (!diagram.diagram_asset_url && !(diagram.expected_labels?.length)) return null;

  const {
    diagram_asset_url,
    diagram_title,
    labeling_instruction,
    expected_labels = [],
    marks_for_diagram,
    tag,
    diagram_id,
  } = diagram;

  const backendOrigin = API.replace(/\/api$/, "");
  const resolvedSrc = diagram_asset_url ? `${backendOrigin}${diagram_asset_url}` : null;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const shuffledOptions = useMemo(() => {
    const a = [...expected_labels];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagram_id]);

  const optionElements = [
    <option key="__placeholder" value="">— Select label —</option>,
    ...shuffledOptions.map((opt, i) => (
      <option key={i} value={opt}>{opt}</option>
    ))
  ];

  return (
    <div className="mb-6 rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{tag || "Diagram"}</span>
          {diagram_title && <span className="text-xs text-slate-700 font-medium">— {diagram_title}</span>}
        </div>
        {marks_for_diagram > 0 && (
          <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-2.5 py-0.5 rounded-full border border-purple-200">
            +{marks_for_diagram} marks for diagram
          </span>
        )}
      </div>

      <div className="bg-white flex flex-col lg:flex-row gap-0">
        {diagram_asset_url && (
          <div className="lg:w-1/2 p-4 flex items-center justify-center border-b lg:border-b-0 lg:border-r border-slate-100">
            <img
              src={resolvedSrc}
              alt={diagram_title || "Diagram"}
              className="max-w-full max-h-[480px] w-full object-contain rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fb = e.currentTarget.nextElementSibling;
                if (fb) fb.style.display = "flex";
              }}
            />
            <div
              className="w-full h-48 bg-slate-50 rounded-lg border border-dashed border-slate-200 flex-col gap-2 items-center justify-center text-slate-400 text-sm"
              style={{ display: "none" }}
            >
              <span className="text-2xl">🖼️</span>
              <span>Diagram image not yet available</span>
              <span className="text-xs text-slate-300">{resolvedSrc}</span>
            </div>
          </div>
        )}

        {expected_labels.length > 0 && (
          <div className={`${diagram_asset_url ? "lg:w-1/2" : "w-full"} p-4 flex flex-col`}>
            <p className="text-xs text-slate-500 mb-3 text-center">
              {labeling_instruction || "Select the correct label for each numbered structure."}
            </p>
            <div className="space-y-2 flex-1 overflow-y-auto max-h-[480px] pr-1">
              {expected_labels.map((correctLabel, idx) => {
                const studentAnswer = labels[idx] || "";
                const isCorrect = feedbackMode && studentAnswer === correctLabel;
                const isWrong   = feedbackMode && !!studentAnswer && studentAnswer !== correctLabel;
                const isBlank   = feedbackMode && !studentAnswer;

                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border
                      ${feedbackMode
                        ? isCorrect ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                        : isWrong   ? "bg-red-100 text-red-600 border-red-300"
                                    : "bg-slate-100 text-slate-400 border-slate-200"
                        : "bg-[#2E3A59] text-white border-[#2E3A59]"
                      }`}>
                      {idx + 1}
                    </span>
                    {feedbackMode ? (
                      <div className="flex-1 flex items-center gap-1 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-lg border flex-1 min-w-[100px]
                          ${isCorrect ? "bg-emerald-50 border-emerald-200 text-emerald-800 font-medium"
                          : isWrong   ? "bg-red-50 border-red-200 text-red-700 line-through"
                                      : "bg-slate-50 border-slate-200 text-slate-400 italic"}`}>
                          {studentAnswer || "Not answered"}
                        </span>
                        {isWrong && (
                          <span className="text-xs px-2 py-1 rounded-lg border bg-emerald-50 border-emerald-200 text-emerald-800 font-medium flex-1 min-w-[100px]">
                            ✓ {correctLabel}
                          </span>
                        )}
                        {isBlank && (
                          <span className="text-xs px-2 py-1 rounded-lg border bg-amber-50 border-amber-200 text-amber-700 flex-1 min-w-[100px]">
                            {correctLabel}
                          </span>
                        )}
                      </div>
                    ) : (
                      <select
                        value={studentAnswer}
                        onChange={(e) => onLabelChange(idx, e.target.value)}
                        className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#2E3A59]/30 focus:border-[#2E3A59] bg-white text-slate-700 cursor-pointer"
                      >
                        {optionElements}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
            {!feedbackMode && (
              <p className="text-xs text-slate-400 mt-3 italic text-center">
                ★ Numbers match the structures on the diagram.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────

const QUOTA_EXHAUSTED_MSG =
  "Your free tier monthly limit has been reached. Please consider upgrading to the Paid Plan to continue practising.";

const PracticePage = () => {
  const navigate = useNavigate();
  const { subject } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const retryQuestionId = location.state?.retryQuestionId || null;

  const [mode, setMode] = useState("single");
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [questionType, setQuestionType] = useState("all");
  const [sprintLength, setSprintLength] = useState(3);

  const [tier, setTier] = useState(() => localStorage.getItem("falco_last_tier") || "T2");
  const handleTierChange = (newTier) => {
    setTier(newTier);
    localStorage.setItem("falco_last_tier", newTier);
  };

  const [filterOptions, setFilterOptions] = useState({
    marks_options: [],
    marks_counts: {},
    total_questions: 0,
  });

  const [sessionActive, setSessionActive] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [attemptedQuestions, setAttemptedQuestions] = useState([]);
  const [excludedIds, setExcludedIds] = useState([]);
  const [sessionStats, setSessionStats] = useState({ done: 0, totalScore: 0, totalMarks: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [diagramLabels, setDiagramLabels] = useState({});
  const [isRetrying, setIsRetrying] = useState(false);

  // ── Upgrade modal ────────────────────────────────────────────────────────────
  const [upgradeOpen, setUpgradeOpen]     = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");

  const openUpgradeModal = (reason = QUOTA_EXHAUSTED_MSG) => {
    setUpgradeReason(reason);
    setUpgradeOpen(true);
  };
  // ────────────────────────────────────────────────────────────────────────────

  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    fetchTopics();
    fetchFilterOptions();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [subject]);

  const fetchTopics = async () => {
    try {
      const res = await axios.get(`${API}/topics/${subject}`);
      setTopics(res.data);
    } catch (e) {
      console.error("Error fetching topics:", e);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const res = await axios.get(`${API}/questions/filters/${subject}`);
      setFilterOptions(res.data);
    } catch (e) {
      console.error("Error fetching filter options:", e);
    }
  };

  const startSession = async () => {
    if (user?.credits_remaining <= 0) {
      openUpgradeModal();
      return;
    }
    try {
      let res;
      if (retryQuestionId) {
        res = await axios.get(`${API}/questions/${retryQuestionId}`);
      } else {
        const params = { exclude_ids: excludedIds.join(",") };
        if (questionType !== "all") params.marks = parseInt(questionType);
        res = await axios.get(`${API}/questions/random/${subject}`, { params });
      }
      const question = res.data;
      setCurrentQuestion(question);
      setSessionActive(true);
      setFeedback(null);
      setAnswer("");
      setDiagramLabels({});

      const marks = question.marks_total || question.marks || 5;
      const time = getTimeForQuestion(marks);
      setTimeRemaining(time);
      startTimeRef.current = Date.now();

      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            toast.warning("Time's up! Submitting your answer...");
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error starting session:", error);
      toast.error(error.response?.data?.detail || "Failed to load question. Try again.");
    }
  };

  const handleSubmit = async () => {
    if (!answer.trim()) {
      toast.error("Please write an answer before submitting");
      return;
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsEvaluating(true);

    const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const payload = {
      question_id: currentQuestion.question_id,
      student_answer: answer,
      mode: mode,
      time_taken_seconds: timeTaken,
      tier: tier,
      diagram_labels: Object.keys(diagramLabels).length > 0 ? diagramLabels : undefined,
      is_retry: isRetrying,
    };

    if (process.env.NODE_ENV === "development") {
      console.group("%c[FALCO EVAL]", "color: #2E3A59; font-weight: bold;");
      console.log("📤 Sending to:", `${API}/evaluate`);
      console.log("📋 Payload:", payload);
    }

    try {
      const res = await axios.post(`${API}/evaluate`, payload);

      if (process.env.NODE_ENV === "development") {
        console.log("✅ evaluation_track:", res.data.evaluation_track, "| score:", res.data.score_awarded, "/", res.data.total_marks);
        console.log("📦 Full response:", res.data);
        console.groupEnd();
      }

      setFeedback(res.data);
      setExcludedIds([...excludedIds, currentQuestion.question_id]);
      setSessionStats((prev) => ({
        done: prev.done + 1,
        totalScore: prev.totalScore + res.data.score_awarded,
        totalMarks: prev.totalMarks + res.data.total_marks,
      }));
      setAttemptedQuestions([...attemptedQuestions, { question: currentQuestion, feedback: res.data }]);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.groupEnd();
      }
      console.error("Error evaluating answer:", error);
      if (error.response?.status === 402) {
        openUpgradeModal();
      } else {
        toast.error(error.response?.data?.detail || "Evaluation failed. Try again.");
      }
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleNextQuestion = async () => {
    setFeedback(null);
    setAnswer("");
    setDiagramLabels({});
    setIsRetrying(false);
    try {
      const params = { exclude_ids: excludedIds.join(",") };
      if (questionType !== "all") params.marks = parseInt(questionType);

      const res = await axios.get(`${API}/questions/random/${subject}`, { params });
      const question = res.data;
      setCurrentQuestion(question);

      const time = getTimeForQuestion(question.marks_total || question.marks);
      setTimeRemaining(time);
      startTimeRef.current = Date.now();

      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) { clearInterval(timerRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error loading next question:", error);
      toast.error("No more questions available");
    }
  };

  const handleRetry = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setFeedback(null);
    setAnswer("");
    setDiagramLabels({});
    setIsRetrying(true);
    const marks = currentQuestion?.marks_total || currentQuestion?.marks || 5;
    const time = getTimeForQuestion(marks);
    setTimeRemaining(time);
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSkip = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setExcludedIds([...excludedIds, currentQuestion.question_id]);
    handleNextQuestion();
  };

  const endSession = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSessionActive(false);
    setCurrentQuestion(null);
    setFeedback(null);
    setAnswer("");
    setDiagramLabels({});
  };

  const wordCount = answer.trim().split(/\s+/).filter(Boolean).length;

  const fbPct = feedback
    ? (feedback.percentage || Math.round((feedback.score_awarded / feedback.total_marks) * 100) || 0)
    : 0;
  const fbColors = pctColors(fbPct);
  const pf = feedback?.feedback && typeof feedback.feedback === "object" ? feedback.feedback : null;
  const fbSummary = pf?.summary || (typeof feedback?.feedback === "string" ? feedback.feedback : null);

  // Filtered breakdown for inline panel
  const inlineBreakdown = feedback?.mark_breakdown_awarded
    ? filterBreakdown(feedback.mark_breakdown_awarded, currentQuestion)
    : [];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* ── HEADER ── */}
      <header className="glass-header sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-600"
                data-testid="practice-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
<div className="flex items-center gap-2">
  <img
    src={falcoLogo}
    alt="AnswerCraft"
    className="h-14 w-14 flex-shrink-0 object-cover object-center"
    style={{ borderRadius: "0" }}
  />
  <span className="text-xl font-extrabold leading-none tracking-tight hidden sm:block">
    <span style={{ color: "#2E3A59" }}>Answer</span><span style={{ color: "#8B6914" }}>Craft</span>
    <sup style={{ color: "#8B6914", fontSize: "0.5em", verticalAlign: "super" }}>™</sup>
  </span>
</div>
            </div>
            <button
              onClick={() => openUpgradeModal("")}
              className="text-sm font-semibold px-4 py-1.5 bg-[#2E3A59] text-white rounded-full hover:bg-[#1A2236] transition-colors"
            >
              Upgrade
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-12 gap-8">

          {/* ── SIDEBAR ── */}
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="bg-white rounded-xl border border-slate-100 p-5 sticky top-24">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2E3A59] to-[#1A2236] flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2E3A59]" data-testid="practice-subject-name">{subject}</h3>
                  <p className="text-xs text-slate-500">RGUHS · 2021-2025</p>
                </div>
              </div>

              {!sessionActive ? (
                <>
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Practice Mode</h4>
                    <div className="space-y-2">
                      <div
                        onClick={() => setMode("single")}
                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                          mode === "single" ? "bg-[#E8EAF0] border-2 border-[#2E3A59]" : "bg-slate-50 border-2 border-transparent"
                        }`}
                        data-testid="mode-single"
                      >
                        <Zap className="w-5 h-5 text-[#2E3A59]" />
                        <div>
                          <p className="font-medium text-sm text-[#2E3A59]">Single Question</p>
                          <p className="text-xs text-slate-500">Immediate feedback</p>
                        </div>
                      </div>
                      <div
                        onClick={() => setMode("sprint")}
                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                          mode === "sprint" ? "bg-[#E8EAF0] border-2 border-[#2E3A59]" : "bg-slate-50 border-2 border-transparent"
                        }`}
                        data-testid="mode-sprint"
                      >
                        <Timer className="w-5 h-5 text-[#2E3A59]" />
                        <div>
                          <p className="font-medium text-sm text-[#2E3A59]">Sprint</p>
                          <p className="text-xs text-slate-500">3-5 Qs, batch eval</p>
                        </div>
                      </div>
                      <div
                        onClick={() => setMode("fullexam")}
                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all opacity-50 ${
                          mode === "fullexam" ? "bg-[#E8EAF0] border-2 border-[#2E3A59]" : "bg-slate-50 border-2 border-transparent"
                        }`}
                        data-testid="mode-fullexam"
                      >
                        <FileText className="w-5 h-5 text-[#2E3A59]" />
                        <div>
                          <p className="font-medium text-sm text-[#2E3A59]">Full Exam</p>
                          <p className="text-xs text-slate-500">Coming soon</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                      Question Type
                      <span className="text-[#C9A227] ml-1">({filterOptions.total_questions} total)</span>
                    </h4>
                    <RadioGroup value={questionType} onValueChange={setQuestionType}>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="all" id="all" />
                          <Label htmlFor="all" className="text-sm flex items-center justify-between w-full">
                            <span>All questions</span>
                            <span className="text-xs text-slate-400">{filterOptions.total_questions}</span>
                          </Label>
                        </div>
                        {filterOptions.marks_options.map((marks) => (
                          <div key={marks} className="flex items-center space-x-2">
                            <RadioGroupItem value={String(marks)} id={`${marks}m`} />
                            <Label htmlFor={`${marks}m`} className="text-sm flex items-center justify-between w-full">
                              <span>{marks}-mark questions</span>
                              <span className="text-xs text-slate-400">
                                {filterOptions.marks_counts[String(marks)] || 0}
                              </span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                  </div>

                  <Button
                    onClick={startSession}
                    className="w-full h-12 bg-[#2E3A59] hover:bg-[#1A2236] font-semibold btn-press"
                    data-testid="start-practice-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Start Practice
                  </Button>
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Session Live</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Questions done</span>
                        <span className="font-semibold text-[#2E3A59]" data-testid="session-done">{sessionStats.done}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Score so far</span>
                        <span className="font-semibold text-[#2E3A59]" data-testid="session-score">
                          {sessionStats.totalMarks > 0
                            ? `${Math.round((sessionStats.totalScore / sessionStats.totalMarks) * 100)}%`
                            : "—"}
                        </span>
                      </div>
                    </div>
                    {sessionStats.done > 0 && (
                      <div className="mt-4">
                        <Progress value={(sessionStats.totalScore / sessionStats.totalMarks) * 100} className="h-2" />
                      </div>
                    )}
                  </div>
                  <Button onClick={endSession} variant="destructive" className="w-full" data-testid="end-session-btn">
                    End Session
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* ── MAIN CONTENT ── */}
          <div className="lg:col-span-8 xl:col-span-9">
            {!sessionActive ? (
              <div className="bg-white rounded-xl border border-slate-100 p-8" data-testid="setup-panel">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-2xl font-bold text-[#2E3A59] mb-2">{subject} Practice</h2>
                  <p className="text-slate-600">RGUHS PYQs 2021–2025 · Select mode and start practising</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  <div className="p-5 bg-slate-50 rounded-xl border-2 border-transparent hover:border-[#C9A227]/50 transition-all cursor-pointer">
                    <Zap className="w-8 h-8 text-[#C9A227] mb-3" />
                    <h3 className="font-semibold text-[#2E3A59] mb-1">Single Question</h3>
                    <p className="text-sm text-slate-500 mb-2">Practice one question at a time with immediate, detailed feedback.</p>
                    <span className="text-xs text-[#C9A227] font-medium">Best for daily practice</span>
                  </div>
                  <div className="p-5 bg-slate-50 rounded-xl border-2 border-transparent hover:border-[#C9A227]/50 transition-all cursor-pointer">
                    <Timer className="w-8 h-8 text-[#C9A227] mb-3" />
                    <h3 className="font-semibold text-[#2E3A59] mb-1">Sprint Mode</h3>
                    <p className="text-sm text-slate-500 mb-2">Attempt 3–5 questions in a focused burst. Feedback after completion.</p>
                    <span className="text-xs text-[#C9A227] font-medium">Best for revision</span>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden mb-6">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <p className="text-sm font-semibold text-[#2E3A59]">Evaluation Mode</p>
                    <p className="text-xs text-slate-500 mt-0.5">Choose how strictly you want to be evaluated</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {[
                      { id: "T1", label: "Learning Mode",   desc: "Build your understanding. Generous scoring to encourage your first attempts.", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-300" },
                      { id: "T2", label: "Exam Ready",       desc: "Practice like the real exam. Calibrated to typical university scoring.",       color: "text-[#2E3A59]",  bg: "bg-blue-50",    border: "border-[#2E3A59]" },
                      { id: "T3", label: "Distinction Mode", desc: "Push your limits. Strict scoring to prepare for top marks.",                   color: "text-purple-700", bg: "bg-purple-50",  border: "border-purple-400" },
                    ].map((t) => (
                      <button
                        key={t.id}
                        onClick={() => handleTierChange(t.id)}
                        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${tier === t.id ? t.bg : "hover:bg-slate-50"}`}
                        data-testid={`tier-btn-${t.id}`}
                      >
                        <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${tier === t.id ? t.border : "border-slate-300"}`}>
                          {tier === t.id && <div className={`w-2 h-2 rounded-full ${t.bg.replace("bg-", "bg-").replace("-50", "-500")}`} />}
                        </div>
                        <div>
                          <span className={`text-sm font-semibold ${tier === t.id ? t.color : "text-slate-600"}`}>{t.label}</span>
                          {tier === t.id && <span className="ml-2 text-xs font-bold text-[#C9A227]">Selected</span>}
                          <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-[#FDF8E6] rounded-xl border border-[#C9A227]/20 mb-6">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#A6851D]">
                      <strong>Tip:</strong> Your first attempt reveals your gaps. Don't aim for perfection — aim to practice.
                      Every question you submit helps identify your weak topics.
                    </p>
                  </div>
                </div>

                <Button
                  onClick={startSession}
                  className="w-full h-14 bg-[#2E3A59] hover:bg-[#1A2236] text-lg font-semibold btn-press"
                  data-testid="launch-practice-btn"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Start Practice
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Timer Bar */}
                <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid="timer-bar">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Time remaining</span>
                    <span className={`font-mono font-bold ${timeRemaining < 60 ? "text-red-500" : "text-[#2E3A59]"}`}>
                      {formatTime(timeRemaining)}
                    </span>
                  </div>
                  <Progress
                    value={(timeRemaining / getTimeForQuestion(currentQuestion?.marks_total || currentQuestion?.marks || 10)) * 100}
                    className="h-2"
                  />
                </div>

                {/* Question Card */}
                <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="question-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      {currentQuestion?.frequency?.label === "HOT" && (
                        <span className="flex items-center gap-1 text-xs font-bold px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                          <Flame className="w-3 h-3" /> HOT
                        </span>
                      )}
                      {currentQuestion?.frequency?.label === "FREQUENT" && (
                        <span className="flex items-center gap-1 text-xs font-bold px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                          <Zap className="w-3 h-3" /> FREQUENT
                        </span>
                      )}
                      <span className="text-sm font-medium text-slate-500">
                        {currentQuestion?.topic_tags?.topic || currentQuestion?.curriculum_mapping?.topic || "General"}
                      </span>
                      <span className="text-slate-300">·</span>
                      <span className="text-sm font-medium text-[#C9A227]">
                        {currentQuestion?.marks_total || currentQuestion?.marks} marks
                      </span>
                      {(currentQuestion?.diagram?.diagram_asset_url || currentQuestion?.diagram?.expected_labels?.length > 0) && (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                            📝 Diagram +{currentQuestion.diagram.marks_for_diagram || currentQuestion.diagram.marks || 2} marks
                          </span>
                        </>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleSkip} className="text-slate-500" data-testid="skip-question-btn">
                      Skip <SkipForward className="w-4 h-4 ml-1" />
                    </Button>
                  </div>

                  {currentQuestion?.source?.years?.length > 0 ? (
                    <div className="mb-3 flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">
                        <BarChart3 className="w-3 h-3" />
                        Asked {currentQuestion.source.times_asked || currentQuestion.source.years.length} time{(currentQuestion.source.times_asked || currentQuestion.source.years.length) !== 1 ? "s" : ""}:
                      </span>
                      <div className="flex gap-1 flex-wrap">
                        {[...currentQuestion.source.years].sort((a, b) => b - a).map((year) => (
                          <span key={year} className="text-xs px-2 py-0.5 bg-[#E8EAF0] text-[#2E3A59] rounded font-medium">{year}</span>
                        ))}
                      </div>
                    </div>
                  ) : currentQuestion?.frequency?.times_asked > 0 ? (
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">
                        <BarChart3 className="w-3 h-3" />
                        Asked {currentQuestion.frequency.times_asked} time{currentQuestion.frequency.times_asked !== 1 ? "s" : ""} in exams
                      </span>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <span className="text-xs text-slate-400 italic">Not found in previous exam papers</span>
                    </div>
                  )}

                  <div className="mb-6">
                    <p className="question-text text-lg text-[#2E3A59] leading-relaxed" data-testid="question-text">
                      {currentQuestion?.question_text}
                    </p>
                  </div>

                  {(currentQuestion?.diagram?.diagram_asset_url || currentQuestion?.diagram?.expected_labels?.length > 0) && (
                    <DiagramPanel
                      diagram={currentQuestion.diagram}
                      labels={diagramLabels}
                      onLabelChange={(idx, val) => setDiagramLabels(prev => ({ ...prev, [idx]: val }))}
                      feedbackMode={!!feedback}
                    />
                  )}

                  {!feedback && (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium text-slate-700">Your Answer</Label>
                        <span className="text-xs text-slate-400" data-testid="word-count">
                          {wordCount} words · <span className={answer.length > 4800 ? "text-red-500 font-semibold" : ""}>{answer.length}/5000</span>
                        </span>
                      </div>
                      <Textarea
                        value={answer}
                        onChange={(e) => { if (e.target.value.length <= 5000) setAnswer(e.target.value); }}
                        placeholder="Write your answer as you would in the exam hall."
                        className="min-h-[200px] answer-area resize-none border-slate-200 focus:border-[#2E3A59]"
                        data-testid="answer-textarea"
                        maxLength={5000}
                      />
                      <div className="flex gap-3 mt-4">
                        <Button
                          onClick={handleSubmit}
                          disabled={isEvaluating || !answer.trim()}
                          className="flex-1 h-12 bg-[#2E3A59] hover:bg-[#1A2236] font-semibold btn-press"
                          data-testid="submit-answer-btn"
                        >
                          {isEvaluating ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              Evaluating...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Check Answer
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* ── FEEDBACK PANEL ── */}
                {feedback && (
                  <div className="bg-white rounded-xl border border-slate-100 overflow-hidden animate-fade-in" data-testid="feedback-panel">
                    <div className={`p-6 ${fbColors.bg}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-600 mb-1">
                            Your Score
                            {feedback.tier_used && (
                              <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-white/60 text-slate-600">
                                {{ T1: "Learning Mode", T2: "Exam Ready", T3: "Distinction" }[feedback.tier_used]}
                              </span>
                            )}
                          </p>
                          <p className="text-4xl font-bold text-[#2E3A59]" data-testid="feedback-score">
                            {feedback.score_awarded}/{feedback.total_marks}
                            {feedback.attempt_number > 1 && (
                              <span className="text-xs font-normal text-slate-400 ml-2">(attempt #{feedback.attempt_number})</span>
                            )}
                          </p>
                          {feedback.readiness_signal && READINESS_CONFIG[feedback.readiness_signal] && (
                            <span className={`inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full border ${READINESS_CONFIG[feedback.readiness_signal].color}`}>
                              {READINESS_CONFIG[feedback.readiness_signal].label}
                            </span>
                          )}
                        </div>
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center ${fbColors.badge}`}>
                          {fbColors.icon}
                        </div>
                      </div>

                      {feedback.tier_scores && (
                        <div className="mt-4 grid grid-cols-3 gap-2" data-testid="tier-comparison-strip">
                          {[
                            { key: "T1", label: "Learning",    borderColor: "border-emerald-300", textColor: "text-emerald-700", activeBg: "bg-emerald-100" },
                            { key: "T2", label: "Exam Ready",  borderColor: "border-blue-300",    textColor: "text-[#2E3A59]",   activeBg: "bg-white" },
                            { key: "T3", label: "Distinction", borderColor: "border-purple-300",  textColor: "text-purple-700",  activeBg: "bg-purple-50" },
                          ].map(({ key, label, borderColor, textColor, activeBg }) => {
                            const isActive = feedback.tier_used === key;
                            const score = feedback.tier_scores[key]?.total ?? "—";
                            return (
                              <div key={key} className={`rounded-lg border-2 px-3 py-2 text-center transition-all ${isActive ? `${borderColor} ${activeBg}` : "border-white/40 bg-white/30"}`}>
                                <p className={`text-xs font-semibold mb-1 ${isActive ? textColor : "text-slate-400"}`}>
                                  {label}{isActive && " ✓"}
                                </p>
                                <p className={`text-lg font-bold ${isActive ? textColor : "text-slate-400"}`}>
                                  {score}<span className="text-xs font-normal">/{feedback.total_marks}</span>
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="p-6 space-y-6">
                      {/* Marks Breakdown — clinical_diagram hidden when not required */}
                      {inlineBreakdown.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-[#2E3A59] mb-3 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-[#C9A227]" /> Marks Breakdown
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {inlineBreakdown.map(([key, value]) => (
                              <div key={key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                <span className="text-xs text-slate-600 capitalize">{key.replace(/_/g, " ")}</span>
                                <span className="text-sm font-semibold text-[#2E3A59]">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {feedback.points_covered?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-[#2E3A59] mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-500" /> Points Covered
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {feedback.points_covered.map((pt, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-semibold text-emerald-700">
                                <CheckCircle className="w-3 h-3" />
                                {pt.length > 45 ? pt.substring(0, 45) + "…" : pt}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {(feedback.points_missed?.length > 0 || feedback.missing_points?.length > 0) && (
                        <div>
                          <h4 className="font-semibold text-[#2E3A59] mb-2 flex items-center gap-2">
                            <XCircle className="w-4 h-4 text-red-500" /> Missing Points
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {(feedback.points_missed || feedback.missing_points || []).map((pt, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 border border-red-200 rounded-lg text-xs font-semibold text-red-600">
                                <XCircle className="w-3 h-3" />
                                {pt.length > 45 ? pt.substring(0, 45) + "…" : pt}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Reason codes — dynamic heading based on score */}
                      {feedback.reason_codes_triggered?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-[#2E3A59] mb-2 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                            {reasonSectionLabel(feedback.score_awarded, feedback.total_marks)}
                          </h4>
                          <div className="space-y-2">
                            {feedback.reason_codes_triggered.map((rc, i) => {
                              const code = typeof rc === "string" ? rc : rc.code;
                              const detail = typeof rc === "object" ? rc.detail : null;
                              return (
                                <div key={i} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-mono font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                                      {REASON_CODE_LABELS[code] || code}
                                    </span>
                                  </div>
                                  {detail && <p className="text-sm text-amber-700">{detail}</p>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {fbSummary && (
                        <div>
                          <h4 className="font-semibold text-[#2E3A59] mb-2 flex items-center gap-2">
                            <Target className="w-4 h-4 text-[#C9A227]" /> Feedback
                          </h4>
                          <p className="text-slate-600 leading-relaxed" data-testid="feedback-text">{fbSummary}</p>
                        </div>
                      )}

                      {(pf?.strengths?.length > 0 || feedback.strengths?.length > 0) && (
                        <div>
                          <h4 className="font-semibold text-[#2E3A59] mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-500" /> Strengths
                          </h4>
                          <ul className="space-y-1">
                            {(pf?.strengths || feedback.strengths || []).map((s, i) => (
                              <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                                <span className="text-emerald-400 mt-1">•</span>{s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {pf?.examiner_tip && (
                        <div className="flex items-start gap-3 p-3 bg-[#FDF8E6] border border-[#C9A227]/30 rounded-xl">
                          <Star className="w-4 h-4 text-[#C9A227] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-bold text-[#A6851D] uppercase tracking-wide mb-1">Examiner's Tip</p>
                            <p className="text-sm text-[#7A5E10]">{pf.examiner_tip}</p>
                          </div>
                        </div>
                      )}

                      {feedback.tier_upgrade_tip && (
                        <div className="flex items-start gap-3 p-3 bg-purple-50 border border-purple-200 rounded-xl" data-testid="tier-upgrade-tip">
                          <TrendingUp className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-1">
                              {{ T1: "To reach Exam Ready →", T2: "To reach Distinction →" }[feedback.tier_used] || "Next Level →"}
                            </p>
                            <p className="text-sm text-purple-700">{feedback.tier_upgrade_tip}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-3 pt-4 border-t">
                        <Button
                          onClick={handleRetry}
                          variant="outline"
                          className="flex-1 h-12 border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white font-semibold transition-colors"
                          data-testid="retry-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" /> Retry
                        </Button>
                        <Button
                          onClick={() => setDetailOpen(true)}
                          variant="outline"
                          className="flex-1 h-12 border-2 border-[#2E3A59] text-[#2E3A59] hover:bg-[#2E3A59] hover:text-white font-semibold transition-colors"
                          data-testid="view-detailed-eval-btn"
                        >
                          <BarChart2 className="w-4 h-4 mr-2" /> Detailed Evaluation
                        </Button>
                        <Button
                          onClick={handleNextQuestion}
                          className="flex-1 h-12 bg-[#2E3A59] hover:bg-[#1A2236] font-semibold btn-press"
                          data-testid="next-question-btn"
                        >
                          Next Question
                        </Button>
                        <Button
                          onClick={() => navigate("/dashboard")}
                          variant="outline"
                          className="h-12 px-4"
                          data-testid="back-to-dashboard-btn"
                        >
                          <Home className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Detailed Evaluation Modal ── */}
      <DetailedEvalModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        feedback={feedback}
        question={currentQuestion}
      />

      {/* ── Upgrade / Quota Exhausted Modal ── */}
      <PaidPlanModal
        open={upgradeOpen}
        onClose={() => { setUpgradeOpen(false); setUpgradeReason(""); }}
        reason={upgradeReason}
      />
    </div>
  );
};

export default PracticePage;